// Copyright © 2021 Move Closer
import { ImageRatio, ImageRatioSizes } from '../_contracts';
import { FALLBACK_IMG_BASE64 } from '../../dsl/bootstrap/Image/Image.config';
/**
 * Resolves the applicable image URL for the current screen resolution.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
export const prepareImage = (imageFile, ratio = null, maxWidth, isLazy = false) => {
    const cdnUrl = 'https://cdn.defence24.pl';
    // Prepare the fallback object in case of any errors.
    let errorReturn = {
        alt: '',
        author: '',
        caption: '',
        height: null,
        width: null,
        src: '',
        srcset: '',
        fullscreen: null
    };
    // Check if the provided `imageFile` object contains its `srcset` property.
    if (typeof imageFile === 'undefined' || imageFile === null) {
        console.error('prepareImage(): Broken [imageFile] object!\nFull [imageFile] object:', imageFile);
        return errorReturn;
    }
    // Extract the required keys.
    const { alt = '', author = '', caption = '' } = imageFile;
    // Update the fallback object with new data.
    errorReturn = {
        ...errorReturn,
        alt,
        author,
        caption
    };
    /**
     * Value for the `<img>'s` `[src]` attribute.
     */
    let src = FALLBACK_IMG_BASE64;
    let height = null;
    let width = null;
    if (typeof imageFile.srcset === 'undefined' ||
        !Array.isArray(imageFile.srcset) ||
        imageFile.srcset.length === 0) {
        try {
            src = imageFile.url;
            if (src && !src.startsWith('http')) {
                src = cdnUrl + src;
            }
        }
        catch (e) {
            console.warn(e);
            src = '';
        }
        // Return the complete object.
        return {
            alt,
            author,
            caption,
            height,
            isLazy,
            src,
            width,
            fullscreen: src
        };
    }
    // Let's found a specific ratio.
    let found = [];
    if (ratio !== null) {
        found = imageFile.srcset.filter(v => {
            return (v.ratio ? v.ratio : ImageRatio.Original) === ratio;
        });
    }
    const originals = imageFile.srcset.filter(v => {
        return (v.ratio ? v.ratio : ImageRatio.Original) === ImageRatio.Original;
    });
    originals.slice().sort((a, b) => a.width - b.width);
    const fullscreen = originals[originals.length - 1];
    if (found.length === 0) {
        found = originals;
    }
    let normalSources = [];
    let retinaSources = [];
    let sources = found.length > 0 ? found : imageFile.srcset;
    // SLICE MUST BE USED TO AVOID MUTATION OF THE ORIGINAL ARRAY
    sources = sources.slice().sort((a, b) => a.width - b.width);
    // filter out images that are too big
    if (typeof maxWidth === 'number') {
        normalSources = sources.filter(({ width }) => width <= maxWidth);
        retinaSources = sources.filter(({ width }) => width <= (2 * maxWidth));
    }
    const normalSource = normalSources[normalSources.length - 1] || sources[0];
    const retinaSource = retinaSources[retinaSources.length - 1] || originals[originals.length - 1];
    if (fullscreen && fullscreen.url && !fullscreen.url.startsWith('http')) {
        fullscreen.url = cdnUrl + fullscreen.url;
    }
    if (normalSource && normalSource.url && !normalSource.url.startsWith('http')) {
        normalSource.url = cdnUrl + normalSource.url;
    }
    if (retinaSource && retinaSource.url && !retinaSource.url.startsWith('http')) {
        retinaSource.url = cdnUrl + retinaSource.url;
    }
    try {
        src = normalSource.url;
        height = normalSource.height;
        width = normalSource.width;
        if (!height && ratio) { // if height is unavailable, but there is a ratio, take dimensions from the ratio registry
            const dim = ImageRatioSizes[ratio][0];
            if (dim && dim.length) {
                height = dim[1];
                width = dim[0];
            }
        }
    }
    catch (error) {
        console.error('prepareImage(): Failed to resolve the applicable [src]!', error);
        return errorReturn;
    }
    let srcset = '';
    // Resolve the value for the `<img>'s` `[srcset]` attribute.
    if (maxWidth && maxWidth < 100) {
        srcset = `${normalSource.url}`;
    }
    else {
        srcset = `${normalSource.url}, ${retinaSource.url} 2x`;
    }
    // Return the complete object.
    return {
        alt,
        author,
        caption,
        height,
        isLazy,
        src,
        srcset,
        width,
        fullscreen: fullscreen ? fullscreen.url : null
    };
};
export default prepareImage;
